/**
 * @module app.core
 */

import { autoinject, bindingMode, bindable } from 'aurelia-framework';
import { ComponentBase, Sorting, StandardLoadOptions, SortDirection, State } from 'app/shared/lib/essette-site';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { CorrespondenceService } from 'app/core/services/correspondence-service';
import { apiCall } from 'app/shared/lib/api/api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { LetterCopyForwardedEvent } from 'app/core/lib/events';
import { ApiQueryResponse } from 'app/shared/lib/essette-api';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { UseContainerCache } from 'app/shared/lib/cache/use-container-cache';
import {
    createCorrespondenceCache, ICorrespondenceCache,
} from 'app/core/caches/correspondence-cache';
import * as Core from 'app/core/models/essette-api';

const LETTER_HISTORY_WINDOW_TRANSITIONAL_PAGE_ID = '613';
const FORWARD_LETTER_COPY_WINDOW_TRANSITIONAL_PAGE_ID = '629';

@autoinject
@UseContainerCache({ cacheFactory: createCorrespondenceCache })
export class CorrespondenceHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable()
    protected cacheKey: string | null = null;
    declare cache: ICorrespondenceCache;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected targetId!: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected memberId!: string;
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected correspondenceConfig: ICorrespondenceConfig = {
        transitionalPageId: '613'
    };

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected allowRetry: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected pageSize: number = 25;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = true;

    protected canPreviewDownloads: boolean = false;
    protected canViewHistory: boolean = false;
    protected allowForward: boolean = false;

    protected correspondenceDetails: Record<string, boolean> = {};

    protected letters: ReadonlyArray<Core.CorrespondenceLetter> = [];
    protected stateRetry: State = new State();
    protected sorting = new Sorting();
    protected resultCount: number = 0;

    private subscriptions: Array<Subscription> = [];

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<Core.CorrespondenceLetter>;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CorrespondenceService,
        private readonly transitional: TransitionalManager,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.targetId) { throw new Error('Bindable property [target-id] was not set'); }
        if(!this.memberId) { throw new Error('Bindable property [member-id] was not set'); }
        this.service.configure(this.correspondenceConfig);

        // letters
        if(!this.cache.isFresh('letterHistories') || this.cache.refreshChildren) {
            this.loadHistoryLetters();
        } else {
            this.loadFromCache();
        }

        this.checkPermissions();

        this.cache.refreshChildren = false; // reset refresh
    }

    protected attached(): void {
        if(this.$table && this.sorting.entries.length > 0) {
            this.$table.applySorting(this.sorting);
        }

        // Subscribe event for letter copy forwards
        this.subscriptions.push(
            this.events.subscribe(LetterCopyForwardedEvent, (e: LetterCopyForwardedEvent) => {
                this.reload();
            })
        );
    }

    ////****************** Events ***************************************************////
    protected pageSizeChanged(): void {
        this.reload();
    }

    protected correspondenceDetailLoaded(event: ExpandEvent<Core.CorrespondenceLetter>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.correspondenceDetails[item.id!] = true;
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canViewHistory = this.accountContext.hasRouteAccess(LETTER_HISTORY_WINDOW_TRANSITIONAL_PAGE_ID);
        this.allowForward = this.accountContext.hasRouteAccess(FORWARD_LETTER_COPY_WINDOW_TRANSITIONAL_PAGE_ID);

        switch(this.correspondenceConfig.moduleCode) {
            case 'CM':
                this.canPreviewDownloads = this.accountContext.hasPermission('CM.CaseCorrespondenceLetter.Download.Preview');
                break;
            case 'UM':
                this.canPreviewDownloads = this.accountContext.hasPermission('UM.AuthCorrespondenceLetter.Download.Preview');
                break;
            default:
                this.canPreviewDownloads = false;
                this.logger.warn(`Unexpected module encountered: ${this.correspondenceConfig.moduleCode}`);
                break;
        }

    }

    public async reload(resetDetails: boolean = false): Promise<void> {
        if(resetDetails) this.correspondenceDetails = {};
        await this.loadHistoryLetters();
    }

    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    protected openLetterHistory(letterId: string): void {
        const w: Window | null = open('', 'Popup_CorrespondenceHistory', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        this.transitional.openTransitional(w, '613', [letterId]);
    }

    protected openLetterPreview(letterId: string) {
        if(!letterId || !this.correspondenceConfig.previewTransitionalPageId) { return; }
        const w: Window | null = open('', 'Popup_PreviewLetter', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        const transitionalIds: string[] = [letterId, '']; //transitional query string in correspondence config is expecting two parameters
        this.transitional.openTransitional(w, this.correspondenceConfig.previewTransitionalPageId, transitionalIds);
    }

    protected loadFromCache(): void {
        if(!!this.cache.letterHistories) this.letters = this.cache.letterHistories;
        if(!!this.cache.letterHistoriesPage) this.$pager.page = this.cache.letterHistoriesPage;
        if(!!this.cache.letterHistoriesSorting) this.sorting = this.cache.letterHistoriesSorting;
        if(!!this.cache.letterHistoriesResultCount) this.resultCount = this.cache.letterHistoriesResultCount;
    }

    protected setCache(): void {
        this.cache.letterHistories = this.letters;
        this.cache.letterHistoriesPage = this.$pager.page;
        this.cache.letterHistoriesSorting = this.sorting;
        this.cache.letterHistoriesResultCount = this.resultCount;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHistoryLetters(): Promise<void> {
        if(!this.targetId || !this.memberId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0
        };

        const includes = [
            'metadata.modified.byUser',
            'status',
            'template',
            'template.version',
            'template.version.language'
        ];

        const fields = [
            'description',
            'metadata.modified.byUser.description',
            'metadata.modified.timestamp',
            'status.description',
            'template.description',
            'template.version.language.description'
        ];

        // UM needs to use the copies include, rather than let the copies component manage its own data
        if(this.correspondenceConfig.moduleCode === 'UM') {
            const copyIncludes = [
                'auth',
                'copies',
                'copies.coverSheet.templateVersion.template',
                'copies.deliveryMethod',
                'copies.delivered.recipient',
                'copies.delivered.to.type',
                'copies.letter',
                'copies.letter.template',
                'copies.status'
            ];

            const copyFields = [
                'auth.number',
                'copies.coverSheet.templateVersion.template.description',
                'copies.delivered.recipient.description',
                'copies.deliveryMethod.code',
                'copies.deliveryMethod.description',
                'copies.status.code',
                'copies.status.description',
                'copies.letter.template.description'
            ];

            includes.push(...copyIncludes);
            fields.push(...copyFields);
        }

        try {
            this.state.beginLoad();

            let response: ApiQueryResponse<Core.CorrespondenceLetter>;
            if(this.correspondenceConfig.rollupByMember) {
                response = await this.service.loadLettersByMember(this.memberId, true, options, includes, fields);
            } else {
                response = await this.service.loadLettersByTarget(this.targetId, true, options, includes, undefined, fields);
            }

            this.letters = response.results;
            this.resultCount = response.count || 0;
            this.setCache();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}

